import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff, Loader2, Info, AlertCircle } from 'lucide-react';
import { userService } from '../../_services';
import * as z from 'zod';

/**
 * Shared ChangePasswordDialog
 *
 * Props
 * ─────
 * open           boolean          Controls dialog visibility
 * onOpenChange   (bool) => void   Called when dialog should open/close
 * mode           'self' | 'admin' 'self' shows current-password field; 'admin' skips it
 * repid          string|number    Required in 'admin' mode – the target user's ID
 * onSuccess      () => void       Optional callback after successful save
 */
const selfSchema = z.object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z.string().min(6, 'At least 6 characters in length'),
    confirmPassword: z.string(),
}).refine(d => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});

const adminSchema = z.object({
    newPassword: z.string().min(6, 'At least 6 characters in length'),
    confirmPassword: z.string(),
}).refine(d => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});

/* ── PasswordField extracted outside the parent so React never remounts it ── */
function PasswordField({ id, name, label, showKey, value, error, show, onChange, onToggleShow }) {
    return (
        <div className="space-y-2">
            <Label htmlFor={id}>{label}</Label>
            <div className="relative">
                <Input
                    id={id}
                    name={name}
                    type={show ? 'text' : 'password'}
                    value={value ?? ''}
                    onChange={onChange}
                    placeholder=""
                    autoComplete="new-password"
                    maxLength={30}
                    className={error ? 'border-destructive pr-10' : 'pr-10'}
                />
                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => onToggleShow(showKey)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
            </div>
            {error && (
                <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{error}
                </p>
            )}
        </div>
    );
}

function ChangePasswordDialog({ open, onOpenChange, mode = 'admin', repid, onSuccess }) {
    const isSelf = mode === 'self';

    const emptyForm = () => isSelf
        ? { currentPassword: '', newPassword: '', confirmPassword: '' }
        : { newPassword: '', confirmPassword: '' };

    const [formData, setFormData] = useState(emptyForm());
    const [errors, setErrors] = useState({});
    const [show, setShow] = useState({ current: false, new: false, confirm: false });
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState('');

    // Reset whenever dialog opens
    useEffect(() => {
        if (open) {
            setFormData(emptyForm());
            setErrors({});
            setShow({ current: false, new: false, confirm: false });
            setApiError('');
        }
    }, [open]);                 // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: undefined }));
        setApiError('');
    };

    const toggleShow = (field) => setShow(prev => ({ ...prev, [field]: !prev[field] }));

    const handleSubmit = async (e) => {
        e.preventDefault();

        const schema = isSelf ? selfSchema : adminSchema;
        const result = schema.safeParse(formData);
        if (!result.success) {
            const fe = result.error.flatten().fieldErrors;
            setErrors(Object.fromEntries(Object.entries(fe).map(([k, v]) => [k, v?.[0]])));
            return;
        }

        setLoading(true);
        try {
            const meta = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = meta.split(':');

            let apidata;
            if (isSelf) {
                apidata = await userService.ResetPassword(
                    userId, userName,
                    formData.currentPassword,
                    formData.newPassword,
                    formData.confirmPassword
                );
            } else {
                apidata = await userService.updateuserpassword(
                    userId, userName, repid, formData.newPassword
                );
            }

            if (apidata?.length > 0) {
                const r = apidata[0];
                if (r.code === '1') {
                    onSuccess?.();
                    onOpenChange(false);
                } else if (r.code === '-1') {
                    setErrors(prev => ({ ...prev, currentPassword: r.message, newPassword: r.message }));
                } else if (r.code === '-2') {
                    setErrors(prev => ({ ...prev, newPassword: r.message }));
                } else {
                    setApiError(r.message ?? 'An error occurred.');
                }
            }
        } catch (err) {
            console.error('ChangePasswordDialog error:', err);
            setApiError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => onOpenChange(false);

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Change Password</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} autoComplete="off">
                    <div className="space-y-4 py-4">
                        {/* Requirements hint */}
                        <Alert>
                            <Info className="h-4 w-4" />
                            <AlertDescription>
                                <p className="font-medium mb-1">Your new password must:</p>
                                <ul className="list-disc list-inside text-sm space-y-0.5">
                                    <li>Be at least 6 characters in length</li>
                                    {isSelf && <li>Not be the same as your current password</li>}
                                </ul>
                            </AlertDescription>
                        </Alert>

                        {/* API-level error */}
                        {apiError && (
                            <p className="text-sm text-destructive flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />{apiError}
                            </p>
                        )}

                        {isSelf && (
                            <PasswordField
                                id="currentPassword"
                                name="currentPassword"
                                label="Current Password"
                                showKey="current"
                                value={formData.currentPassword}
                                error={errors.currentPassword}
                                show={show.current}
                                onChange={handleChange}
                                onToggleShow={toggleShow}
                            />
                        )}

                        <PasswordField
                            id="newPassword"
                            name="newPassword"
                            label="New Password"
                            showKey="new"
                            value={formData.newPassword}
                            error={errors.newPassword}
                            show={show.new}
                            onChange={handleChange}
                            onToggleShow={toggleShow}
                        />

                        <PasswordField
                            id="confirmPassword"
                            name="confirmPassword"
                            label="Confirm Password"
                            showKey="confirm"
                            value={formData.confirmPassword}
                            error={errors.confirmPassword}
                            show={show.confirm}
                            onChange={handleChange}
                            onToggleShow={toggleShow}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading
                                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving…</>
                                : 'Save Changes'
                            }
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default ChangePasswordDialog;
